import React, { useMemo } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Legend,
  LabelList,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const DemographicsCharts = ({ genderByFieldData }) => {
  const cleanedGenderData = useMemo(
    () =>
      (genderByFieldData || [])
        .filter((item) => item && item.studyField)
        .map((item) => ({
          ...item,
          femalePercent: Number(item.femalePercent) || 0,
          malePercent: Number(item.malePercent) || 0,
        }))
        .filter((item) => item.femalePercent !== 0 || item.malePercent !== 0),
    [genderByFieldData]
  );

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {/* 🔵 Gender Balance by Field */}
      <Grid item xs={12}>
        <Card sx={{ height: 700, display: "flex", flexDirection: "column" }}>
          <CardContent
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Gender Balance by Field
            </Typography>

            {cleanedGenderData.length > 0 ? (
              <Box
                sx={{
                  flex: 1,
                  overflowX: "auto",
                  overflowY: "hidden",
                  minHeight: 400,
                }}
              >
                <Box
                  sx={{
                    minWidth: Math.max(800, cleanedGenderData.length * 80),
                    height: "100%",
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={cleanedGenderData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="studyField"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        fontSize={11}
                        interval={0}
                      />
                      <YAxis
                        fontSize={12}
                        label={{
                          value: "Percentage (%)",
                          angle: -90,
                          position: "insideLeft",
                        }}
                        domain={[0, 100]}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          `${value}%`,
                          name === "Female %" ? "Female" : "Male",
                        ]}
                      />
                      <Legend verticalAlign="top" height={36} />
                      <Bar
                        dataKey="femalePercent"
                        fill="#e91e63"
                        name="Female %"
                        barSize={40}
                      >
                        <LabelList
                          dataKey="femalePercent"
                          position="top"
                          formatter={(value) => `${value}%`}
                          style={{
                            fill: "#e91e63",
                            fontSize: "10px",
                            fontWeight: "bold",
                          }}
                        />
                      </Bar>
                      <Bar
                        dataKey="malePercent"
                        fill="#2196f3"
                        name="Male %"
                        barSize={40}
                      >
                        <LabelList
                          dataKey="malePercent"
                          position="top"
                          formatter={(value) => `${value}%`}
                          style={{
                            fill: "#2196f3",
                            fontSize: "10px",
                            fontWeight: "bold",
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 400,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No gender distribution data available
                </Typography>
              </Box>
            )}
          </CardContent>

          {/* Gender Summary */}
          {cleanedGenderData.length > 0 && (
            <Box
              sx={{
                p: 2,
                bgcolor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
                maxHeight: cleanedGenderData.length > 8 ? "200px" : "auto",
                overflowY: cleanedGenderData.length > 8 ? "auto" : "visible",
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Gender Distribution by Field:
              </Typography>

              {cleanedGenderData.map((entry, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ flex: 1, mr: 1 }}>
                    {entry.studyField}:
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ color: "#e91e63" }}>
                      ♀ {entry.femalePercent}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#2196f3" }}>
                      ♂ {entry.malePercent}%
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

export default DemographicsCharts;
